/**
 * Read access to the context-graph projections (DOP-416/417).
 *
 * Three scopes: the whole deal room, the neighbourhood behind one answer, and
 * the whole conversation (every answer's neighbourhood in one chat, unioned).
 * Both go through `apiFetch`, are validated at the boundary, and never throw —
 * a caller gets a result it can render, not an exception it has to guard.
 *
 * The result is a small union rather than the `Graph | null` the sibling
 * `cabinetService` returns, because the page has to tell three states apart:
 * a graph, "the backend has no projection for this yet" (the routers answer
 * 501 until the API tickets land, and that is a quiet notice, not an error),
 * and an actual failure.
 */

import { contextGraphFixture } from "../fixtures/contextGraph.fixture";
import { type ContextGraph, parseContextGraph } from "../types/contextGraph";
import { apiFetch } from "../utils/apiClient";

export type ContextGraphResult =
	| { status: "ok"; graph: ContextGraph }
	/** 501 — the projection is not built yet. */
	| { status: "unavailable" }
	| { status: "error" };

/**
 * Dev-only fixture mode: `http://localhost:3000/context-graph?fixture=1`
 * renders the canonical fixture without touching the API.
 *
 * Two guards keep this out of production, matching `cabinetService`:
 * `NODE_ENV === "development"` (CRA hard-codes `"production"` in
 * `npm run build`, so the branch is stripped) and the explicit opt-in query
 * param. It short-circuits BEFORE the request rather than acting as a
 * fallback, so it can never mask a live API error.
 */
function shouldUseFixture(): boolean {
	if (process.env.NODE_ENV !== "development") return false;
	if (typeof window === "undefined") return false;
	return new URLSearchParams(window.location.search).get("fixture") === "1";
}

async function fetchGraph(path: string): Promise<ContextGraphResult> {
	if (shouldUseFixture()) return { status: "ok", graph: contextGraphFixture };
	try {
		const resp = await apiFetch(path);
		if (resp.status === 501) return { status: "unavailable" };
		if (!resp.ok) return { status: "error" };
		const graph = parseContextGraph(await resp.json());
		return graph === null ? { status: "error" } : { status: "ok", graph };
	} catch {
		return { status: "error" };
	}
}

class ContextGraphService {
	/** The deal room's whole graph, optionally centred on one node. */
	async getRoomGraph(
		projectId: string,
		focus?: string | null,
	): Promise<ContextGraphResult> {
		const query = focus ? `?focus=${encodeURIComponent(focus)}` : "";
		return fetchGraph(
			`/api/v3/context-graph/rooms/${encodeURIComponent(projectId)}${query}`,
		);
	}

	/**
	 * Every answer in one conversation, as one graph.
	 *
	 * An answer is built on the ones before it — the leverage test on the
	 * EBITDA walk that produced its denominator — and the per-message graph
	 * cannot show that. The chat graph is the union, with the same figure
	 * cited in two answers drawn once.
	 */
	async getChatGraph(chatPublicId: string): Promise<ContextGraphResult> {
		return fetchGraph(
			`/api/v3/context-graph/chats/${encodeURIComponent(chatPublicId)}`,
		);
	}

	/** The neighbourhood behind one answer's verified citations. */
	async getMessageGraph(
		chatPublicId: string,
		messageId: string,
	): Promise<ContextGraphResult> {
		return fetchGraph(
			`/api/v3/context-graph/chats/${encodeURIComponent(chatPublicId)}` +
				`/messages/${encodeURIComponent(messageId)}`,
		);
	}
}

export const contextGraphService = new ContextGraphService();
export default contextGraphService;
